import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Gem, Lock, ArrowRight, Check, AlertTriangle } from "lucide-react";

const AuthShell = ({ children, title, sub }) => (
  <div className="min-h-screen flex">
    <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-white/5">
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse 60% 50% at 30% 40%, rgba(244,208,63,0.18), transparent 60%), radial-gradient(ellipse 50% 40% at 70% 70%, rgba(91,44,111,0.3), transparent 60%)"
      }} />
      <div className="noise-overlay" />
      <div className="relative flex flex-col justify-between p-12 w-full">
        <Link to="/" className="flex items-center gap-2.5">
          <Gem className="w-7 h-7 text-gold" />
          <span className="font-display text-xl font-semibold text-white">My Leads Convert</span>
        </Link>
        <div>
          <h2 className="font-display text-4xl text-white leading-tight mb-4">
            Choose a new password. You'll be back in your workspace in one click.
          </h2>
          <div className="text-sm text-zinc-400">Use 6+ characters. A password manager is always a good idea.</div>
        </div>
        <div className="text-xs text-zinc-600 font-mono">my leads convert · real records · grounded drafts</div>
      </div>
    </div>
    <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
      <div className="w-full max-w-md">
        <div className="lg:hidden mb-8">
          <Link to="/" className="flex items-center gap-2.5">
            <Gem className="w-7 h-7 text-gold" />
            <span className="font-display text-xl font-semibold text-white">My Leads Convert</span>
          </Link>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white mb-2">{title}</h1>
        <p className="text-zinc-400 mb-8">{sub}</p>
        {children}
      </div>
    </div>
  </div>
);

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthShell title="Invalid link" sub="This reset link is missing its token.">
        <div className="glass-card p-5 flex items-start gap-3 border-amber-400/30 bg-amber-400/[0.04]" data-testid="invalid-token">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-zinc-300">
            Please use the full link from your reset email, or request a new one.
          </div>
        </div>
        <Link to="/forgot-password" className="btn-primary w-full mt-6" data-testid="request-new-link">
          Request a new link <ArrowRight className="w-4 h-4" />
        </Link>
      </AuthShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthShell title="Password updated" sub="You're all set. Redirecting you to sign in...">
        <div className="glass-card p-5 flex items-start gap-3 border-emerald-500/30 bg-emerald-500/[0.04]" data-testid="reset-success">
          <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-zinc-300">
            Your password has been updated. Use the new one to sign in.
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" sub="Make it strong — at least 6 characters.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <label className="label-eyebrow block mb-2">New password</label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-zinc-500" />
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-glass pl-10"
              placeholder="At least 6 characters"
              data-testid="reset-password"
            />
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Confirm password</label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-zinc-500" />
            <input
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="input-glass pl-10"
              placeholder="Re-enter password"
              data-testid="reset-confirm"
            />
          </div>
        </div>
        {error && <div className="text-sm text-rose-400" data-testid="reset-error">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="reset-submit">
          {busy ? "Updating..." : <>Update password <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
    </AuthShell>
  );
}
